import { useMemo, useState } from "react";
import Breadcrumb from "@/components/base/Breadcrumb";
import AnimeCard from "@/components/base/AnimeCard";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { animeList } from "@/mocks/anime";

const PAGE_SIZE = 6;
const CURRENT_YEAR = 2026;

const genreOptions = ["全部", "战斗", "奇幻", "科幻", "冒险", "治愈", "剧情", "爱情", "校园", "悬疑", "音乐", "美食", "机战"];
const regionOptions = ["全部", "日本", "中国", "欧美"];
const yearOptions = [
  { value: "all", label: "全部年代" },
  { value: "recent", label: "近一年" },
  { value: "mid", label: "1-3 年" },
  { value: "old", label: "3 年以上" },
];
const ratingOptions = [
  { value: 0, label: "全部评分" },
  { value: 9, label: "9.0 分以上" },
  { value: 8.5, label: "8.5 分以上" },
  { value: 8, label: "8.0 分以上" },
];
const sortOptions = [
  { value: "popularity", label: "热度" },
  { value: "new", label: "新番" },
  { value: "rating", label: "评分" },
  { value: "updated", label: "更新时间" },
];

export default function AnimeListPage() {
  useDocumentMeta(
    "动漫作品大全 - h动漫",
    "浏览h动漫作品大全，涵盖冒险、爱情、科幻等多种类型的动漫作品，支持类型、地区、年代与评分多维筛选与排序。"
  );

  const [genre, setGenre] = useState("全部");
  const [region, setRegion] = useState("全部");
  const [yearRange, setYearRange] = useState("all");
  const [minRating, setMinRating] = useState(0);
  const [sort, setSort] = useState("popularity");
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    let list = animeList.filter((a) => (genre === "全部" ? true : a.genre.includes(genre)));
    if (region !== "全部") list = list.filter((a) => a.region === region);
    if (yearRange === "recent") list = list.filter((a) => a.year >= CURRENT_YEAR - 1);
    if (yearRange === "mid") list = list.filter((a) => a.year >= CURRENT_YEAR - 3 && a.year < CURRENT_YEAR - 1);
    if (yearRange === "old") list = list.filter((a) => a.year < CURRENT_YEAR - 3);
    if (minRating > 0) list = list.filter((a) => a.rating >= minRating);

    list = [...list].sort((a, b) => {
      if (sort === "popularity") return b.popularity - a.popularity;
      if (sort === "new") return b.year - a.year;
      if (sort === "rating") return b.rating - a.rating;
      return b.updatedAt.localeCompare(a.updatedAt);
    });
    return list;
  }, [genre, region, yearRange, minRating, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const reset = () => {
    setGenre("全部");
    setRegion("全部");
    setYearRange("all");
    setMinRating(0);
    setSort("popularity");
    setPage(1);
  };

  const chip = (active: boolean) =>
    `px-3 py-1.5 rounded-md text-xs cursor-pointer transition-colors ${
      active ? "bg-primary-500 text-background-50 font-medium" : "bg-background-100 text-foreground-700 hover:bg-background-200"
    }`;

  return (
    <div className="w-full px-4 md:px-6 py-6 md:py-8">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "动漫作品" }]} />
      <header className="mt-4">
        <h1 className="font-heading font-extrabold text-foreground-950 text-2xl md:text-3xl">动漫作品大全</h1>
        <p className="mt-2 text-sm md:text-base text-foreground-600 max-w-3xl leading-relaxed">
          收录各类动画作品，覆盖冒险、爱情、科幻、治愈等多种类型。支持按类型、地区、年代与评分区间筛选，
          并提供热度、新番、评分、更新时间多种排序方式。
        </p>
      </header>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
        <aside className="lg:sticky lg:top-20 self-start space-y-4">
          <div className="rounded-lg border border-background-200/70 bg-background-50 p-4 space-y-4">
            <h2 className="flex items-center gap-2 font-heading font-semibold text-foreground-950 text-sm">
              <i className="ri-filter-3-line text-primary-500" />
              筛选面板
            </h2>

            <div>
              <p className="text-xs font-medium text-foreground-700 mb-2">类型</p>
              <div className="flex flex-wrap gap-1.5">
                {genreOptions.map((g) => (
                  <button
                    key={g}
                    type="button"
                    onClick={() => {
                      setGenre(g);
                      setPage(1);
                    }}
                    className={chip(genre === g)}
                  >
                    {g}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <p className="text-xs font-medium text-foreground-700 mb-2">地区</p>
              <div className="flex flex-wrap gap-1.5">
                {regionOptions.map((r) => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => {
                      setRegion(r);
                      setPage(1);
                    }}
                    className={chip(region === r)}
                  >
                    {r}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <p className="text-xs font-medium text-foreground-700 mb-2">年代</p>
              <div className="flex flex-wrap gap-1.5">
                {yearOptions.map((y) => (
                  <button
                    key={y.value}
                    type="button"
                    onClick={() => {
                      setYearRange(y.value);
                      setPage(1);
                    }}
                    className={chip(yearRange === y.value)}
                  >
                    {y.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <p className="text-xs font-medium text-foreground-700 mb-2">评分区间</p>
              <div className="flex flex-wrap gap-1.5">
                {ratingOptions.map((r) => (
                  <button
                    key={r.value}
                    type="button"
                    onClick={() => {
                      setMinRating(r.value);
                      setPage(1);
                    }}
                    className={chip(minRating === r.value)}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            </div>

            <button
              type="button"
              onClick={reset}
              className="w-full h-9 rounded-md border border-background-300 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              重置筛选
            </button>
          </div>
        </aside>

        <div className="min-w-0">
          <div className="mb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <p className="text-sm text-foreground-600">
              共 <span className="font-semibold text-foreground-900">{filtered.length}</span> 部作品
            </p>
            <div className="flex items-center gap-2">
              <span className="text-xs text-foreground-600 whitespace-nowrap">排序</span>
              <div className="flex flex-wrap gap-1.5">
                {sortOptions.map((s) => (
                  <button
                    key={s.value}
                    type="button"
                    onClick={() => setSort(s.value)}
                    className={`px-3 py-1.5 rounded-md text-xs cursor-pointer transition-colors ${
                      sort === s.value
                        ? "bg-accent-500 text-background-50 font-medium"
                        : "bg-background-100 text-foreground-700 hover:bg-background-200"
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {pageItems.length > 0 ? (
            <>
              <div data-product-shop className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
                {pageItems.map((anime) => (
                  <AnimeCard key={anime.id} anime={anime} />
                ))}
              </div>
              <div className="mt-8">
                <Pagination current={safePage} total={totalPages} onChange={setPage} />
              </div>
            </>
          ) : (
            <EmptyState
              icon="ri-tv-2-line"
              title="无符合条件作品"
              description="当前筛选条件下没有匹配的作品，试试放宽筛选范围。"
              actionLabel="重置筛选"
              onAction={reset}
            />
          )}
        </div>
      </div>
    </div>
  );
}